// M4 Custody & Transfer — Phase 1 FE api layer.
//
// Plain fetch(apiUrl(...)) calls: in maket mode MSW intercepts these exact
// paths (mirrors the backend 1:1); in real-engine mode (USE_REAL_API) the
// global auth-fetch wrapper attaches the bearer token and the real backend
// answers (gated by settings.custody_enabled). No per-call mock branch needed.

import { apiUrl } from '../lib/api'

export type CustodyNode = {
  id: string
  node_kind: string
  name: string | null
  code: string | null
  active: boolean
}

export type CustodyTransfer = {
  id: string
  transfer_kind: string
  status: string
  origin_node_id: string
  dest_node_id: string
  expected_unit_count: number
  received_unit_count: number
  master_tracking_number: string | null
  master_carrier_code: string | null
  feature_flow: string | null
  departed_at: string | null
  created_at: string
}

export type CustodyContainer = {
  id: string
  container_kind: string
  status: string
  transfer_id: string | null
  expected_child_count: number
  scanned_child_count: number
  sealed_at: string | null
  created_at: string
}

export type CustodyPackage = {
  id: string
  nature: string
  status: string
  internal_qr: string | null
  last_mile_tracking_number: string | null
  container_id: string | null
  created_at: string
}

export type CustodyEvent = {
  id: string
  occurred_at: string
  subject_kind: string
  subject_id: string
  event_type: string
  from_status: string | null
  to_status: string | null
  scan_method: string
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { detail?: { code?: string; message?: string } }
      | null
    const detail = body?.detail
    const err = new Error(detail?.message ?? `request failed: ${res.status}`) as Error & {
      code?: string
      status?: number
    }
    err.code = detail?.code
    err.status = res.status
    throw err
  }
  return res.json() as Promise<T>
}

export async function createNode(body: {
  node_kind: string
  name?: string
  code?: string
}): Promise<CustodyNode> {
  const res = await fetch(apiUrl('/custody/nodes'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow<CustodyNode>(res)
}

export async function createTransfer(body: {
  transfer_kind: string
  origin_node_id: string
  dest_node_id: string
  expected_unit_count?: number
  feature_flow?: string
}): Promise<CustodyTransfer> {
  const res = await fetch(apiUrl('/custody/transfers'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow<CustodyTransfer>(res)
}

export async function getTransfer(id: string): Promise<CustodyTransfer> {
  return jsonOrThrow<CustodyTransfer>(await fetch(apiUrl(`/custody/transfers/${id}`)))
}

export async function departTransfer(
  id: string,
  body: { master_tracking_number?: string; master_carrier_code?: string },
): Promise<CustodyTransfer> {
  const res = await fetch(apiUrl(`/custody/transfers/${id}/depart`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow<CustodyTransfer>(res)
}

export async function createPackages(
  packages: Array<{
    nature: string
    order_id?: string
    internal_qr?: string
    last_mile_tracking_number?: string
  }>,
): Promise<CustodyPackage[]> {
  const res = await fetch(apiUrl('/custody/packages'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ packages }),
  })
  return jsonOrThrow<CustodyPackage[]>(res)
}

export async function createContainer(body: {
  container_kind: string
  transfer_id?: string
  expected_child_count?: number
}): Promise<CustodyContainer> {
  const res = await fetch(apiUrl('/custody/containers'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow<CustodyContainer>(res)
}

export async function scanIntoContainer(
  containerId: string,
  packageId: string,
): Promise<CustodyContainer> {
  const res = await fetch(apiUrl(`/custody/containers/${containerId}/scan-in`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ package_id: packageId }),
  })
  return jsonOrThrow<CustodyContainer>(res)
}

export async function sealContainer(
  containerId: string,
  overrideCountGate = false,
): Promise<CustodyContainer> {
  const res = await fetch(apiUrl(`/custody/containers/${containerId}/seal`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ override_count_gate: overrideCountGate }),
  })
  return jsonOrThrow<CustodyContainer>(res)
}

export async function listEvents(
  subjectKind: 'package' | 'container' | 'transfer',
  subjectId: string,
): Promise<{ items: CustodyEvent[] }> {
  const url = apiUrl(
    `/custody/events?subject_kind=${subjectKind}&subject_id=${subjectId}`,
  )
  return jsonOrThrow<{ items: CustodyEvent[] }>(await fetch(url))
}

// ── Phase 2 — hub receipt + reconcile + discrepancy ──

export type ReconcileResult = {
  transfer_id: string
  status: string
  expected: number
  received: number
  missing: number
  discrepancy_id: string | null
}

export type CustodyDiscrepancy = {
  id: string
  case_kind: string
  status: string
  transfer_id: string | null
  expected_vs_received: { expected?: number; received?: number; missing?: number }
  financial_outcome_kind: string | null
  resolution_notes: string | null
  sla_due_at: string | null
  created_at: string
}

export async function listTransferPackages(transferId: string): Promise<CustodyPackage[]> {
  return jsonOrThrow<CustodyPackage[]>(
    await fetch(apiUrl(`/custody/transfers/${transferId}/packages`)),
  )
}

export async function masterScan(transferId: string): Promise<CustodyTransfer> {
  return jsonOrThrow<CustodyTransfer>(
    await fetch(apiUrl(`/custody/transfers/${transferId}/master-scan`), { method: 'POST' }),
  )
}

export async function innerScan(transferId: string, packageId: string): Promise<CustodyPackage> {
  const res = await fetch(apiUrl(`/custody/transfers/${transferId}/inner-scan`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ package_id: packageId }),
  })
  return jsonOrThrow<CustodyPackage>(res)
}

export async function reconcile(transferId: string): Promise<ReconcileResult> {
  return jsonOrThrow<ReconcileResult>(
    await fetch(apiUrl(`/custody/transfers/${transferId}/reconcile`), { method: 'POST' }),
  )
}

export async function listDiscrepancies(transferId?: string): Promise<{ items: CustodyDiscrepancy[] }> {
  const q = transferId ? `?transfer_id=${transferId}` : ''
  return jsonOrThrow<{ items: CustodyDiscrepancy[] }>(await fetch(apiUrl(`/custody/discrepancies${q}`)))
}

// ── Phase 3 induction + Phase 4 finance accruals ──

export type InductResult = {
  transfer_id: string
  inducted: number
  marketplace_pushed: number
}

export type CustodyAccrual = {
  id: string
  accrual_kind: string
  status: string
  amount_usd: string
  responsible_org_id: string | null
  beneficiary_org_id: string | null
  transfer_id: string | null
  discrepancy_id: string | null
  approved_at: string | null
  notes: string | null
  created_at: string
}

export async function induct(transferId: string): Promise<InductResult> {
  return jsonOrThrow<InductResult>(
    await fetch(apiUrl(`/custody/transfers/${transferId}/induct`), { method: 'POST' }),
  )
}

export async function listAccruals(transferId?: string): Promise<{ items: CustodyAccrual[] }> {
  const q = transferId ? `?transfer_id=${transferId}` : ''
  return jsonOrThrow<{ items: CustodyAccrual[] }>(await fetch(apiUrl(`/custody/accruals${q}`)))
}

export async function createAccrual(body: {
  accrual_kind: string
  amount_usd: string
  transfer_id?: string
  notes?: string
}): Promise<CustodyAccrual> {
  const res = await fetch(apiUrl('/custody/accruals'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow<CustodyAccrual>(res)
}

export async function openChargeback(
  discrepancyId: string,
  body: { amount_usd: string; notes?: string },
): Promise<CustodyAccrual> {
  const res = await fetch(apiUrl(`/custody/discrepancies/${discrepancyId}/chargeback`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return jsonOrThrow<CustodyAccrual>(res)
}

export async function approveAccrual(accrualId: string): Promise<CustodyAccrual> {
  return jsonOrThrow<CustodyAccrual>(
    await fetch(apiUrl(`/custody/accruals/${accrualId}/approve`), { method: 'POST' }),
  )
}

export async function voidAccrual(accrualId: string): Promise<CustodyAccrual> {
  return jsonOrThrow<CustodyAccrual>(
    await fetch(apiUrl(`/custody/accruals/${accrualId}/void`), { method: 'POST' }),
  )
}
